/// <reference types="@types/googlemaps" />
import { Component, OnInit, ViewChild, AfterViewInit, NgZone, Inject, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { RegistrationService } from '../shared/registration.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { Driver } from 'src/app/shared/models/driver.model';
import { environment } from 'src/environments/environment';
import { first, catchError } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { SubSink } from 'subsink';
import { tap, onErrorResumeNext } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { ProfilePicture } from 'src/app/shared/models/profile-picture.model';
import { RegistrationState, DriverStateModel } from 'src/app/registration/state/registration.state';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { of } from 'rxjs';
import { VEHICLE_TYPE } from '../constants';
import { DobAgeValidator } from 'src/app/shared/custom-form-validators/dob-age.validator';
import { CANADA_PROVINCES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { ROUTE_DRIVER_DASHBOARD } from 'src/app/shared/routes';
import { ToastService } from 'src/app/shared/toast/toast.service';
class AddressModel {
  fullAddress: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}

const REQUIRED_FIELD_MESSAGE = 'Required';
const validAddressMessage = 'Please enter a full valid address';
const MaximumLengthFieldMessage = (value: number) => `Must be less than ${value} characters`;
const MinimumLengthFieldMessage = (value: number) => `Must be atleast ${value} characters`;
const DEFAULT_PROFILE_IMAGE = '/assets/images/default-profile-picture.png';
const DOB_AGE_ERROR_MESSAGE = 'You must be at least 18 years old to proceed.';
const ValidPhoneFormat = 'Must be ###-###-####';
const REQUEST_SUCCESS = 'Successfully updated profile.';
const REQUEST_ERROR = 'Something went wrong. Please try again.';
const CANADA_WORK_STATUES = [
  {
    name: 'canWorkPermit',
    displayName: 'Work Permit'
  },
  {
    name: 'canResident',
    displayName: 'Resident'
  },
  {
    name: 'canCitizen',
    displayName: 'Citizen'
  }
];
const USA_WORK_STATUES = [
  {
    name: 'usWorkVisa',
    displayName: 'Work Visa'
  },
  {
    name: 'usResident',
    displayName: 'Resident'
  },
  {
    name: 'usCitizen',
    displayName: 'Citizen'
  }
];
const COUNTRY_WORK_STATUS = {
  Canada: CANADA_WORK_STATUES,
  USA: USA_WORK_STATUES
};
@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./personal-information.component.scss'],
    standalone: false
})
export class PersonalInformationComponent implements OnInit, AfterViewInit, OnDestroy {
  form: UntypedFormGroup;
  loading: boolean;
  errorMessage: string;
  maxDate: Date = new Date();
  phone: string;
  email: string;
  driverID: string;
  isPersonalInfoCompleted: boolean;
  prefillPersonalData: boolean;
  showNavBar: boolean = true;
  driverState: DriverStateModel;
  private driver: any;
  addressModel: AddressModel;
  workStatues = [];
  private subs = new SubSink();
  private profilePicturePath: ProfilePicture = new ProfilePicture();
  profilePicture: any;
  @ViewChild('addressText', { static: false }) addressText: any;
  @ViewChild('firstName', { static: false }) firstNameInput: any;
  isGoogleAddressLookupEnabled: boolean = true; // Default value for toggle
  isGoogleLookupReinitPending: boolean = false; // Track reinitialization
  countries = [
    { value: 'CA', key: 'Canada' },
    { value: 'US', key: 'USA' }
  ];
  selectedCountry: string;
  provinces = [];
  canadaProvinces = CANADA_PROVINCES;
  usaProvinces = USA_PROVINCES;

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly zone: NgZone,
    private readonly fb: UntypedFormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly appService: AppService,
    private readonly store: Store,
    private readonly dateTimeService: DateTimeService,
    private readonly dobAgeValidator: DobAgeValidator,
    private readonly router: Router,
    private readonly toastService: ToastService,
    @Inject('environmentData') public environment: any
  ) {
    this.addressModel = new AddressModel();
    this.loading = false;
    this.errorMessage = undefined;
    this.activatedRoute.url.subscribe(params => {
      if (params[0]) {
        this.prefillPersonalData = false;
      } else {
        this.prefillPersonalData = true;
      }
    });
  }

  ngOnInit() {
    this.form = this.createForm();
    this.driver = this.registrationService.getDriver();
    this.subs.add(
      this.registrationService
        .loadDriver()
        .pipe(
          onErrorResumeNext(
            this.registrationService.loadProfilePicture().pipe(
              catchError(error => {
                return of(false);
              })
            )
          ),
          tap(() => {
            this.profilePicturePath = this.store.selectSnapshot(RegistrationState.profilePicture);
          }),
          tap(() => (this.driver = this.store.selectSnapshot(RegistrationState.driver))),
          catchError(error => {
            return of(false);
          })
        )
        .subscribe(() => {
          if (this.profilePicturePath && this.profilePicturePath.fileUrl) {
            this.profilePicture = this.profilePicturePath.fileUrl;
          } else {
            this.profilePicture = DEFAULT_PROFILE_IMAGE;
          }
          if (this.isGoogleAddressLookupEnabled) {
            this.getPlaceAutocomplete(this.addressText);
          }
        })
    );
    if (this.driver) {
      this.driverID = this.driver.driverID;
      this.email = this.driver.email;
      this.phone = this.driver.phoneNumber;
      this.populateForm(this.driver);
    }
    if (this.appService.isNativeApp) {
      this.showNavBar = false;
    }
  }

  ngAfterViewInit() {
    if (this.isGoogleAddressLookupEnabled) {
      this.getPlaceAutocomplete(this.addressText);
    }

    setTimeout(() => {
      this.firstNameInput?.nativeElement?.focus();
    }, 0);
  }

  ngAfterViewChecked() {
    if (this.isGoogleLookupReinitPending && this.addressText?.nativeElement) {
      this.getPlaceAutocomplete(this.addressText);
      this.isGoogleLookupReinitPending = false; // Reset the flag
    }
  }

  onNavigateToProfile(): void {
    this.registrationService.navigateToProfile();
  }
  goBack(): void {
    this.location.back();
  }
  onNavigateToProfilePicture(): void {
    this.registrationService.navigateToProfilePicture(this.activatedRoute);
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.validateAllFormFields(this.form);
      return;
    }
    this.loading = true;
    this.errorMessage = undefined;
    const driverModel = this.prepareSaveModel(this.driver, this.form);
    this.subs.add(
      this.registrationService
        .updateDriver(driverModel)
        .pipe(first())
        .subscribe(
          (driver: Driver) => {
            this.driver = driver;
            this.toastService.showSuccess(REQUEST_SUCCESS);
            this.errorMessage = undefined;
            this.loading = false;
            // Navigate to dashboard after successful profile update
            this.router.navigate([ROUTE_DRIVER_DASHBOARD]);
          },
          (error: string) => {
            this.toastService.showError(REQUEST_ERROR);
            this.errorMessage = environment.errorMessage;
            this.loading = false;
          }
        )
    );
  }
  get dateOfBirth() {
    return this.form.get('dateOfBirth');
  }
  get gender() {
    return this.form.get('gender');
  }
  get address() {
    return this.form.get('address');
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      dateOfBirth: [
        '',
        Validators.compose([(ctrl: UntypedFormControl) => this.dobAgeValidator.validate(ctrl), Validators.required])
      ],
      gender: ['', Validators.required],
      address: [
        '',
        [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(
            "^[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*,[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*, [A-Z]{2} [A-Z0-9 ]*, [A-Za-z]+$"
          )
        ]
      ],
      streetAddress: [''],
      city: [''],
      province: [''],
      zipCode: [''],
      country: [''],
      phone: ['', [Validators.required, Validators.minLength(12)]],
      workStatus: ['', [Validators.required]],
      email: [''],
      id: ['']
    });
    form.addControl('emergencyContacts', this.fb.array([this.createRequiredEmergencyContactFormGroup()]));

    return form;
  }

  createRequiredEmergencyContactFormGroup(): UntypedFormGroup {
    return this.fb.group({
      emergencyContactName: ['', Validators.required],
      emergencyContactRelation: ['', Validators.required],
      emergencyContactPhone: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(12)]]
    });
  }

  createEmergencyContactFormGroup(): UntypedFormGroup {
    return this.fb.group({
      emergencyContactName: ['', Validators.required],
      emergencyContactRelation: ['', Validators.required],
      emergencyContactPhone: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(12)]]
    });
  }

  addEmergencyContact() {
    const emergencyContacts = this.form.get('emergencyContacts') as UntypedFormArray;
    emergencyContacts.push(this.createEmergencyContactFormGroup());
  }

  get emergencyContacts(): UntypedFormArray {
    return this.form.get('emergencyContacts') as UntypedFormArray;
  }

  getDateOfBirthErrorMessage(): string {
    const errors = this.dateOfBirth?.errors;
    return errors?.invalidAge ? DOB_AGE_ERROR_MESSAGE : errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getGenderErrorMessage(): string {
    return this.gender.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getAddressErrorMessage(): string {
    return this.address.errors.required
      ? REQUIRED_FIELD_MESSAGE
      : this.address.errors.pattern
        ? validAddressMessage
        : this.address.errors.maxlength
          ? MaximumLengthFieldMessage(this.address.errors.maxlength.requiredLength)
          : '';
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
  onDatePickerClick(datepicker: any) {
    datepicker.open();
  }

  private prepareSaveModel(driver: Driver, form: UntypedFormGroup): Driver {
    const formModel = form.value;

    if (!this.isGoogleAddressLookupEnabled) {
      // If Google Address Lookup is disabled, combine the separate fields into a full address
      const street = formModel.streetAddress || '';
      const city = formModel.city || '';
      const province = formModel.province || '';
      const zip = formModel.zipCode || '';
      const country = formModel.country || '';

      formModel.address = `${street}, ${city}, ${province} ${zip}, ${country}`; // Concatenate address parts
    }
    this.addressModel = this.parseAddress(formModel.address);
    const numbersOnlyPhoneNumber = formModel.phone.replace(/-/g, '');

    const filteredEmergencyContacts = formModel.emergencyContacts
      .map((contact: any) => ({
        ...contact,
        emergencyContactPhone: contact.emergencyContactPhone.replace(/-/g, '')
      }))
      .filter((contact: any) => {
        return (
          contact.emergencyContactName?.trim() ||
          contact.emergencyContactPhone?.trim() ||
          contact.emergencyContactRelation?.trim()
        );
      });

    const formData = {
      birthday: this.dateTimeService.formatDateTime(formModel.dateOfBirth, 'YYYY-MM-DD'),
      gender: formModel.gender,
      address: formModel.address,
      city: this.addressModel.city,
      state: this.addressModel.state,
      zip: this.addressModel.zipcode,
      country: this.addressModel.country,
      phoneNumber: numbersOnlyPhoneNumber,
      firstName: formModel.firstName,
      lastName: formModel.lastName,
      emergencyContacts: filteredEmergencyContacts,
      workStatus: formModel.workStatus
    } as Driver;

    if (driver?.assignedVehicleType) {
      driver.assignedVehicleType = VEHICLE_TYPE.find(type => type.displayName === driver.assignedVehicleType)?.name;
    }

    const driverToSave = Object.assign({}, driver, formData);
    delete driverToSave.licenseType;
    delete driverToSave.issuingAuthority;
    delete driverToSave.sin;
    return driverToSave;
  }
  private populateForm(model: Driver): void {
    let localBirthdayTime = new Date(model.birthday);
    localBirthdayTime.setMinutes(localBirthdayTime.getMinutes() + localBirthdayTime.getTimezoneOffset());
    this.form.patchValue({
      firstName: model ? (model.firstName ? model.firstName : '') : '',
      lastName: model ? (model.lastName ? model.lastName : '') : '',
      gender: model ? (model.gender ? model.gender : '') : '',
      dateOfBirth: model ? (localBirthdayTime ? localBirthdayTime : '') : '',
      address: model ? (model.address ? model.address : '') : '',
      phone: model ? (model.phoneNumber ? this.formatPhoneNumberForDisplay(model.phoneNumber) : '') : '',
      email: model ? (model.email ? model.email : '') : '',
      id: model ? model.id : '',
      workStatus: model ? (model.workStatus ? model.workStatus : '') : ''
    });
    const emergencyContactsFormArray = this.form.get('emergencyContacts') as UntypedFormArray;
    emergencyContactsFormArray.clear();

    if (model.emergencyContacts && Array.isArray(model.emergencyContacts)) {
      model.emergencyContacts.forEach(contact => {
        emergencyContactsFormArray.push(
          this.fb.group({
            emergencyContactName: [contact.emergencyContactName || '', Validators.required],
            emergencyContactRelation: [contact.emergencyContactRelation || '', Validators.required],
            emergencyContactPhone: [this.formatPhoneNumberForDisplay(contact.emergencyContactPhone) || '', [Validators.required, Validators.minLength(12), Validators.maxLength(12)]]
          })
        );
      });
    }

    if (emergencyContactsFormArray.length === 0) {
      emergencyContactsFormArray.push(this.createRequiredEmergencyContactFormGroup());
    }
    this.workStatues = COUNTRY_WORK_STATUS[model.country] || CANADA_WORK_STATUES;
  }

  private getPlaceAutocomplete(addresstext: any): void {
    if (!addresstext?.nativeElement) {
      return;  // Check if addressText is available
    }
    const input = addresstext.nativeElement;
    
    // Clean up existing autocomplete instance if it exists
    if (input.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(input);
      input.autocompleteInstance = null;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(addresstext.nativeElement, {
        componentRestrictions: {
          country: ['US', 'CA']
        },
        types: [],
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        // Check if place has required data
        if (place && place.formatted_address) {
          this.zone.run(() => {
            try {
              this.form.patchValue({ address: place.formatted_address });

              const addressModel = this.parseAddress(place.formatted_address);
              this.workStatues = COUNTRY_WORK_STATUS[addressModel.country] || CANADA_WORK_STATUES;

              if (addressModel.country !== this.driver.country) {
                this.form.patchValue({ workStatus: '' });
              }
            } catch (error) {
              console.error('Error processing place selection:', error);
            }
          });
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

      // Store autocomplete instance in the element for later removal
      input.autocompleteInstance = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }

  parseAddress(address: string): AddressModel {
    const parsedAddressArr = address.split(',');
    const addressModel = new AddressModel();
    if (address && parsedAddressArr.length === 4) {
      addressModel.fullAddress = address;
      addressModel.city = parsedAddressArr[1]?.trim();
      addressModel.state = parsedAddressArr[2]?.trim();
      addressModel.zipcode = addressModel.state.substring(3, addressModel.state.length)?.trim();
      addressModel.state = addressModel.state.split(' ')[0];
      addressModel.country = parsedAddressArr[3]?.trim();
    }

    return addressModel;
  }

  private validateAllFormFields(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }
  private formatPhoneNumberForDisplay(phoneNumber: string): string {
    if (phoneNumber.length > 6)
      return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;

    return phoneNumber;
  }
  onGoogleAddressLookupToggle(event: any) {
    this.isGoogleAddressLookupEnabled = !this.isGoogleAddressLookupEnabled;

    if (!this.isGoogleAddressLookupEnabled) {
      // Disable Google Address Lookup and full address field
      this.form.get('address').clearValidators();
      this.form.get('address').updateValueAndValidity();

      // Enable separate address field validations
      this.form.get('streetAddress').setValidators([Validators.required]);
      this.form.get('city').setValidators([Validators.required]);
      this.form.get('province').setValidators([Validators.required]);
      this.form.get('zipCode').setValidators([Validators.required]);
      this.form.get('country').setValidators([Validators.required]);

      const input = this.addressText?.nativeElement; // Check if addressText is available
      if (input?.autocompleteInstance) {
        google.maps.event.clearInstanceListeners(input);
        delete input.autocompleteInstance;
      }
    } else {
      // Enable Google Address Lookup and full address field
      this.form.get('address').setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(
          "^[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*,[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*, [A-Z]{2} [A-Z0-9 ]*, [A-Za-z]+$"
        )
      ]);
      // Clear separate address field validations
      ['streetAddress', 'city', 'province', 'zipCode', 'country'].forEach(field => {
        this.form.get(field).clearValidators();
        this.form.get(field).updateValueAndValidity();
      });

      // Clear address field to avoid default commas
      this.form.get('address').setValue('');

      if (!this.addressText?.nativeElement) {
        this.isGoogleLookupReinitPending = true; // Schedule reinitialization
      } else {
        this.getPlaceAutocomplete(this.addressText);
      }
    }
  }
  onCountryChange(): void {
    this.selectedCountry = this.form.get('country').value;
    let country;
    if(this.selectedCountry === 'CA'){
      country = 'Canada'
    } else if (this.selectedCountry === 'US'){
      country = "USA"
    }
    this.workStatues = COUNTRY_WORK_STATUS[country] || CANADA_WORK_STATUES;
    this.setProvinces();
  }
  setProvinces(): void {
    if (this.selectedCountry === 'CA') {
      this.provinces = this.canadaProvinces;
    } else if (this.selectedCountry === 'US') {
      this.provinces = this.usaProvinces;
    } else {
      this.provinces = [];
    }
  }
}